import config from '../config/app';
import AWS from 'aws-sdk';
import { getCredentials } from './session';
import routes from '../routes'

export const role_attr = "custom:role";
export const role_admin = "admin";
export const role_general = 'general';
export const roles = {
    [role_admin]: "管理者",
    [role_general]: "一般",
};

/**
 * ユーザーのリストを取得
 * @param params
 * @returns {Promise}
 */
export const getUsers = async (params) => {
    return new Promise(async(resolve, reject) => {
        // cognitoからユーザを取得
        const data = await getActiveUsers();

        let list = [];
        for (let item of data) {
            // 部分一致検索の判定
            // Cognitoのユーザ検索は 完全一致・前方一致 しかないので、取得後のこのタイミングで絞り込み
            if (params.search && params.search !== "") {
                // 検索文字列がヒットしない場合はスキップ
                if (item.Username.indexOf(params.search) === -1) {
                    continue;
                }
            }
            const role = checkAdminRoleByAttribute(item.Attributes)? role_admin: role_general;
            const user = {
                id: getSub(item.Attributes),
                name: item.Username,
                role: role,
                createdAt: item.UserCreateDate,
                updatedAt: item.UserLastModifiedDate,
            };
            list.push(user);
        }

        const total = list.length;
        const offset = params.offset ? params.offset: 0;
        const end = (params.count && offset + params.count < total) ? offset + params.count: total;
        list = list.slice(offset, end);

        resolve({
            total: total,
            list: list,
        });
    });
};

/**
 * Cognitoからユーザを取得
 * @param token
 * @returns {Promise<CognitoIdentityServiceProvider.UsersListType>}
 */
export const getActiveUsers = async (token = '') => {
    // クレデンシャルを設定
    const credentials = await getCredentials();
    const cognitoidp = new AWS.CognitoIdentityServiceProvider( {
        credentials: credentials,
        region: config.aws.region
    });

    // パラメータを設定
    const params = {
        UserPoolId: config.aws.cognito.UserPoolId,
        Limit: 60,
        Filter: "cognito:user_status='CONFIRMED'"
    };

    // 再帰処理用
    if (token) params.PaginationToken = token;

    // cognitoからユーザを取得
    const data = await cognitoidp.listUsers(params).promise();

    if (!data.PaginationToken) {
        return data.Users
    }

    // listUsersは最大60件しか取得できないので、再帰的に取得
    const nextItems = await getActiveUsers(data.PaginationToken);
    const users = data.Users;
    Array.prototype.push.apply(users, nextItems);
    return users;
};

/**
 * Cognitoユーザー作成
 * @param param
 * @returns {Promise}
 */
export const createUser = async (param) => {
    return new Promise(async(resolve, reject) => {
        const credentials = await getCredentials();
        const cognitoidp = new AWS.CognitoIdentityServiceProvider( {
            credentials: credentials,
            region: config.aws.region
        });
        const params = {
            UserPoolId: config.aws.cognito.UserPoolId,
            Username: param.name,
            TemporaryPassword: param.password,
            MessageAction: "SUPPRESS",
            UserAttributes: [
                {
                    Name: "email",
                    Value: "dummy@dummy.jp",
                },
                {
                    Name: "custom:isAdmin",
                    Value: "true",
                },
                {
                    Name: "custom:role",
                    Value: param.role,
                },
            ]
        };

        cognitoidp.adminCreateUser(params).promise()
            .then(user => {
                return cognitoidp.adminSetUserPassword({
                    UserPoolId: config.aws.cognito.UserPoolId,
                    Username: param.name,
                    Password: param.password,
                    Permanent: true
                }).promise();
            })
            .then(() => {
                resolve();
            })
            .catch(err => {
                console.log(err);
                reject(err);
            });
    });
};

/**
 * Cognitoユーザ属性、パスワード変更
 * @param param
 * @returns {Promise}
 */
export const editUser = async (param) => {
    return new Promise(async(resolve, reject) => {
        const credentials = await getCredentials();
        const cognitoidp = new AWS.CognitoIdentityServiceProvider({
            credentials: credentials,
            region: config.aws.region
        });
        const params = {
            UserPoolId: config.aws.cognito.UserPoolId,
            Username: param.name,
            UserAttributes: [
                {
                    Name: "custom:role",
                    Value: param.role,
                },
            ]
        };

        cognitoidp.adminUpdateUserAttributes(params).promise()
            .then(user => {
                return cognitoidp.adminSetUserPassword({
                    UserPoolId: config.aws.cognito.UserPoolId,
                    Username: param.name,
                    Password: param.password,
                    Permanent: true
                }).promise();
            })
            .then(() => {
                resolve();
            })
            .catch(err => {
                console.log(err);
                reject(err);
            });
    });
};

/**
 * Cognitoユーザ削除
 * @param param
 * @returns {Promise}
 */
export const deleteUser = async (param) => {
    return new Promise(async(resolve, reject) => {
        const credentials = await getCredentials();
        const cognitoidp = new AWS.CognitoIdentityServiceProvider({
            credentials: credentials,
            region: config.aws.region
        });
        const params = {
            UserPoolId: config.aws.cognito.UserPoolId,
            Username: param.name,
        };

        cognitoidp.adminDeleteUser(params).promise()
            .then(user => {
                resolve();
            })
            .catch(err => {
                console.log(err);
                reject(err);
            });
    });
};

/**
 * Cognitoユーザ削除（複数）
 * @param params
 * @returns {*}
 */
export const batchDelete = (params) => {
    const promises = params.map(param => {
        return deleteUser(param);
    });

    return Promise.all(promises);
};

/**
 * 管理者権限チェック
 * @param param
 * @returns {boolean}
 */
export const checkAdminRole = param => {
    return param && param === role_general;
};

/**
 * ユーザ属性で管理者権限チェック
 * @param attributes
 * @returns {boolean}
 */
export const checkAdminRoleByAttribute = attributes => {
    let roleAdmin = Object.keys(roles)[0];
    if (attributes && attributes.length > 0) {
        for (let attribute of attributes) {
            if (attribute.Name === role_attr) {
                return attribute.Value === roleAdmin;
            }
        }
    }
    return true;
};

/**
 * ユーザ属性からsubを取得
 * @param attributes
 * @returns {string|*}
 */
export const getSub = attributes => {
    if (attributes && attributes.length > 0) {
        for (let attribute of attributes) {
            if (attribute.Name === "sub") {
                return attribute.Value;
            }
        }
    }
    return "";
};

/**
 * セッションストレージの属性で管理者権限チェック
 * @returns {boolean}
 */
export const checkAdminRoleBySession = () => {
    const role = JSON.parse(sessionStorage.getItem('attributes'))[role_attr];
    if (role) return role === role_admin;
    return true;
};

/**
 * 一般ユーザ用のルート判定
 * @param path
 * @returns {boolean}
 */
export const isGeneralRoute = path => {
    for (let route of routes) {
        if (route.general) {
            for (let child of route.children) {
                if (child.path === path && child.general) return true;
            }
        }
    }
    return false;
};



// WEBPACK FOOTER //
// ./src/api/cognitoUser.js